import type { ProposalNodeSet } from '../types';
import { escapeHtml } from './supplierTimberExport';
import { deckingLinearFeet, getStructureDeckingItems } from './proposalStructureDecking';
import { waitForDocumentImages, waitForNextPaint } from './waitForDocumentImages';

export { deckingLinearFeet };

/** Minimal proposal shape required for the decking supplier request (per-structure decking only). */
export interface SupplierDeckingExportState {
  nodeSets: ProposalNodeSet[];
}

export interface DeckingSupplierRow {
  structure: string;
  product: string;
  widthInches: number;
  sqFt: number;
  linearFeet: number;
}

function nodeSetsIncludedInProposal(nodeSets: ProposalNodeSet[]): ProposalNodeSet[] {
  return nodeSets.filter(ns => ns.enabled !== false);
}

/**
 * One row per decking item on each enabled structure (sqFt > 0 and width > 0), in
 * proposal order. A structure with several decking sizes yields several rows.
 * Widths come from the shared resolver (nominal-name derivation) so both new and
 * legacy proposals report the correct board width.
 */
export function deckingSupplierRows(state: SupplierDeckingExportState): DeckingSupplierRow[] {
  return nodeSetsIncludedInProposal(state.nodeSets).flatMap(ns =>
    getStructureDeckingItems(ns.materials)
      .filter(item => (item.sqFt || 0) > 0 && (item.widthInches || 0) > 0)
      .map(item => ({
        structure: ns.name,
        product: item.productName?.trim() || '',
        widthInches: item.widthInches,
        sqFt: item.sqFt,
        linearFeet: deckingLinearFeet(item.sqFt, item.widthInches),
      })),
  );
}

export function deckingExportHasContent(state: SupplierDeckingExportState): boolean {
  return deckingSupplierRows(state).length > 0;
}

export const SUPPLIER_DECKING_CSV_COLUMNS = [
  'Structure',
  'Product',
  'Board Width (in)',
  'Sq Ft',
  'Linear Feet',
] as const;

function csvCell(value: string): string {
  return `"${value.replace(/"/g, '""')}"`;
}

/** Supplier-format decking CSV — one row per structure, quantities in linear feet, no pricing. */
export function buildDeckingSupplierCsvText(state: SupplierDeckingExportState): string {
  const rows: string[] = [SUPPLIER_DECKING_CSV_COLUMNS.join(',')];
  for (const row of deckingSupplierRows(state)) {
    rows.push(
      [
        csvCell(row.structure),
        csvCell(row.product),
        String(row.widthInches),
        String(row.sqFt),
        String(row.linearFeet),
      ].join(','),
    );
  }
  return rows.join('\n');
}

export function downloadDeckingSupplierCsv(state: SupplierDeckingExportState, filenameBase: string): void {
  const csv = buildDeckingSupplierCsvText(state);
  const blob = new Blob([csv], { type: 'text/csv' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `${filenameBase.replace(/[^\w\-]+/g, '_') || 'decking-request'}.csv`;
  a.click();
  URL.revokeObjectURL(url);
}

function deckingSupplierLogoUrl(): string {
  const base = typeof window !== 'undefined' ? window.location.origin : '';
  const prefix = (process.env.PUBLIC_URL || '').replace(/\/$/, '');
  return `${base}${prefix}/assets/logo/LCTF%20Profile.png`;
}

/** Printable decking request HTML, branded like the timber RFQ, sorted by structure. */
export function buildDeckingSupplierRequestHtml(
  state: SupplierDeckingExportState,
  dealTitle: string | undefined,
  generatedAt: Date = new Date(),
  logoUrl?: string,
): string {
  const logoSrc = logoUrl ?? deckingSupplierLogoUrl();
  const rows = deckingSupplierRows(state);
  const totalLinearFeet = rows.reduce((sum, row) => sum + row.linearFeet, 0);

  let html = `
      <html>
      <head>
        <title>Decking Request - ${escapeHtml(dealTitle || 'Proposal')}</title>
        <style>
          body { font-family: Arial, sans-serif; padding: 20px; }
          .header { display: flex; align-items: center; gap: 20px; margin-bottom: 20px; padding-bottom: 20px; border-bottom: 2px solid #2c3e2f; }
          .logo { width: 80px; height: 80px; object-fit: contain; }
          .company-info { flex: 1; }
          .company-name { font-size: 20px; font-weight: bold; color: #2c3e2f; margin: 0 0 4px 0; }
          .company-contact { font-size: 11px; color: #555; margin: 2px 0; }
          .company-contact a { color: #2c3e2f; text-decoration: none; }
          h1 { color: #2c3e2f; margin-bottom: 5px; font-size: 18px; }
          table { width: 100%; border-collapse: collapse; margin-bottom: 20px; }
          th { background: #2c3e2f; color: white; padding: 8px; text-align: left; font-size: 12px; }
          td { border: 1px solid #ddd; padding: 6px 8px; font-size: 11px; }
          tr:nth-child(even) { background: #f9f9f9; }
          .totals { font-weight: bold; background: #f0f0f0; }
          .date { color: #666; font-size: 12px; }
          .project { color: #444; font-size: 14px; margin-bottom: 10px; }
          .note { background: #fff3cd; border: 1px solid #ffc107; padding: 12px 16px; border-radius: 6px; margin: 16px 0 24px 0; font-size: 12px; font-weight: 600; color: #856404; }
          .empty { color: #666; font-style: italic; margin: 24px 0; }
        </style>
      </head>
      <body>
        <div class="header">
          <img src="${escapeHtml(logoSrc)}" class="logo" alt="LCTF Logo" onerror="this.style.display='none'" />
          <div class="company-info">
            <p class="company-name">Leiper's Creek Timber Frames</p>
            <p class="company-contact"><a href="https://www.lctimberframes.com">www.lctimberframes.com</a></p>
            <p class="company-contact"><a href="mailto:info@lctimberframes.com">info@lctimberframes.com</a></p>
            <p class="company-contact">615-716-8490</p>
          </div>
        </div>
        <h1>Decking - Request for Quote</h1>
        <p class="project"><strong>Project:</strong> ${escapeHtml(dealTitle || 'Proposal')}</p>
        <p class="date">Generated: ${generatedAt.toLocaleDateString()}</p>
        <div class="note">ALL DECKING QUANTITIES ARE IN LINEAR FEET.</div>
    `;

  if (rows.length === 0) {
    html += `<p class="empty">No decking on enabled structures. Add decking (square feet and board width) in structure details, then export again.</p>`;
    html += `</body></html>`;
    return html;
  }

  html += `<table>
      <thead><tr><th>Structure</th><th>Product</th><th>Board Width (in)</th><th>Sq Ft</th><th>Linear Feet</th></tr></thead>
      <tbody>`;
  rows.forEach(row => {
    html += `<tr>
        <td>${escapeHtml(row.structure)}</td>
        <td>${escapeHtml(row.product)}</td>
        <td>${row.widthInches}</td>
        <td>${row.sqFt.toLocaleString()}</td>
        <td>${row.linearFeet.toLocaleString()}</td>
      </tr>`;
  });
  html += `<tr class="totals">
      <td colspan="4">Total linear feet</td>
      <td>${totalLinearFeet.toLocaleString()}</td>
    </tr>`;
  html += `</tbody></table>`;

  html += `</body></html>`;
  return html;
}

/** Write decking request HTML into an already-open window and trigger print. */
export async function printDeckingSupplierToWindow(
  printWindow: Window,
  state: SupplierDeckingExportState,
  dealTitle: string | undefined,
  generatedAt?: Date,
): Promise<void> {
  const html = buildDeckingSupplierRequestHtml(state, dealTitle, generatedAt);
  printWindow.document.open();
  printWindow.document.write(html);
  printWindow.document.close();
  await waitForDocumentImages(printWindow.document, 4000);
  await waitForNextPaint();
  printWindow.focus();
  printWindow.print();
}
